<div
  class="chat-conversation d-flex flex-column h-100 position-relative"
  [class.chat-conversation--compact]="compact"
  (dragenter)="onDragEnter($event)"
  (dragover)="onDragOver($event)"
  (dragleave)="onDragLeave($event)"
  (drop)="onDrop($event)"
>
  <!-- Drag-and-drop overlay (T-33) — pointer-events off, events bubble to the host -->
  @if (dragActive) {
    <div
      class="chat-dropzone position-absolute top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center gap-2 rounded"
    >
      <i class="ri-upload-cloud-2-line font-24 text-primary"></i>
      <span class="fw-semibold text-primary">{{
        "chat.conversation.dropToSend" | translate
      }}</span>
    </div>
  }

  <!-- Header -->
  <div
    class="chat-conversation__header border-bottom px-3 py-2 d-flex align-items-center gap-2 flex-shrink-0"
  >
    @if (showBack) {
      <!-- Phone master/detail (chat app): back to the conversation list. -->
      <button
        type="button"
        class="btn btn-sm btn-light chat-conversation__header-btn flex-shrink-0"
        (click)="back.emit()"
        [attr.aria-label]="'chat.conversation.back' | translate"
      >
        <i class="ri-arrow-left-line" aria-hidden="true"></i>
      </button>
    }
    <!-- Same avatar tile as the list and the message rows. Direct conversations
         show the person (photo/initials + presence dot); channels show the type
         glyph. The old Online/Offline sub-line is gone — the dot carries it. -->
    <span class="chat-avatar-tile flex-shrink-0">
      @if (isDirect) {
        @if (headerImage) {
          <img [src]="headerImage" [alt]="headerName" />
        } @else {
          {{ headerInitial }}
        }
        <span
          class="chat-avatar-tile__presence rounded-circle"
          [ngClass]="headerPresence?.dotClass ?? 'bg-secondary'"
          aria-hidden="true"
        ></span>
      } @else {
        <i [ngClass]="headerIcon" aria-hidden="true"></i>
      }
    </span>
    <div class="flex-grow-1 overflow-hidden">
      @if (canViewMembers) {
        <!-- Channel/group title is a button: clicking it opens the
             "Participantes" roster dialog (PUBLIC included — the roster is
             company-readable, like the preview). -->
        <button
          type="button"
          class="chat-conversation__title-button d-block w-100 text-start"
          (click)="openMembers()"
          [matTooltip]="'chat.conversation.viewMembers' | translate"
          [attr.aria-label]="'chat.conversation.viewMembers' | translate"
        >
          <h5 class="m-0 d-flex align-items-center gap-2">
            <span class="chat-name">{{ headerName }}</span>
            @if (conversation?.archived) {
              <span class="badge bg-soft-warning text-warning flex-shrink-0">
                {{ "chat.conversation.archived" | translate }}
              </span>
            }
          </h5>
          <small class="text-muted d-block text-truncate">
            @if (memberCount > 0) {
              {{
                "chat.conversation.members" | translate: { count: memberCount }
              }}
            } @else {
              {{ "chat.conversation.viewMembers" | translate }}
            }
          </small>
        </button>
      } @else {
        <h5 class="m-0 d-flex align-items-center gap-2">
          <span class="chat-name">{{ headerName }}</span>
          @if (conversation?.archived) {
            <span class="badge bg-soft-warning text-warning flex-shrink-0">
              {{ "chat.conversation.archived" | translate }}
            </span>
          }
        </h5>
        @if (isDirect && headerStatusLine) {
          <small class="text-muted d-block text-truncate">{{
            headerStatusLine
          }}</small>
        }
      }
    </div>

    <!--
      In-conversation search. A bare magnifier beside the contact card — no button
      surface in any state, so the header colour shows through it. The field lives
      in the dropdown so the header keeps one line, and the hits are listed right
      under it; picking one jumps to the message in place instead of swapping the
      pane for a results screen.

      `autoClose="outside"` is what makes it usable: Bootstrap's default closes the
      menu on any click inside it, which would dismiss the dropdown on the first
      keystroke in the input.
    -->
    @if (channelId) {
      <div class="dropdown flex-shrink-0">
        <button
          type="button"
          class="btn btn-sm chat-conversation__search-toggle dropdown-toggle arrow-none"
          data-bs-toggle="dropdown"
          data-bs-auto-close="outside"
          aria-expanded="false"
          [attr.aria-label]="'chat.bubble.searchInConversation' | translate"
          [matTooltip]="'chat.bubble.searchInConversation' | translate"
        >
          <i class="ri-search-line"></i>
        </button>
        <div class="dropdown-menu dropdown-menu-end chat-search-menu p-2">
          <div
            class="chat-search-menu__field d-flex align-items-center gap-2 px-2 rounded"
          >
            <i class="ri-search-line flex-shrink-0" aria-hidden="true"></i>
            <input
              type="search"
              #searchBox
              class="chat-search-menu__input flex-grow-1"
              [value]="searchTerm"
              (input)="search(searchBox.value)"
              [placeholder]="'chat.bubble.searchInConversation' | translate"
              [attr.aria-label]="'chat.bubble.searchInConversation' | translate"
            />
            @if (searching) {
              <span
                class="spinner-border spinner-border-sm flex-shrink-0"
                role="status"
                aria-hidden="true"
              ></span>
            } @else if (searchTerm) {
              <button
                type="button"
                class="btn btn-sm p-0 border-0 bg-transparent text-muted flex-shrink-0"
                (click)="clearSearch(); searchBox.focus()"
                [attr.aria-label]="'chat.search.clearFilters' | translate"
              >
                <i class="ri-close-line" aria-hidden="true"></i>
              </button>
            }
          </div>

          @if (searchHits.length > 0) {
            <div class="chat-search-menu__hits">
              @for (hit of searchHits; track hit.messageId) {
                <button
                  type="button"
                  class="dropdown-item chat-search-menu__hit rounded"
                  (click)="openSearchHit(hit)"
                >
                  <span class="d-flex align-items-center gap-2">
                    <small class="fw-semibold chat-name">{{
                      hit.authorName
                    }}</small>
                    <small class="text-muted flex-shrink-0">{{
                      hit.timeLabel
                    }}</small>
                  </span>
                  <small class="d-block text-truncate">
                    @for (
                      segment of hit.segments;
                      track trackSegment($index, segment)
                    ) {
                      @if (segment.match) {
                        <mark class="chat-search-menu__mark">{{
                          segment.text
                        }}</mark>
                      } @else {
                        <span>{{ segment.text }}</span>
                      }
                    }
                  </small>
                </button>
              }
            </div>
          } @else if (noSearchHits) {
            <p class="text-muted text-center mb-0 mt-2">
              <small>{{ "chat.search.empty" | translate }}</small>
            </p>
          }
        </div>
      </div>
    }

    <!-- Conversation overflow menu (T-35 · RF-04/RF-05 · AC-04) -->
    @if (hasHeaderActions) {
      <div class="dropdown flex-shrink-0">
        <button
          type="button"
          class="btn btn-sm btn-light chat-conversation__header-btn dropdown-toggle arrow-none"
          data-bs-toggle="dropdown"
          aria-expanded="false"
          [attr.aria-label]="'chat.channel.menu' | translate"
          [matTooltip]="'chat.channel.menu' | translate"
        >
          <i class="ri-more-2-fill"></i>
        </button>
        <div class="dropdown-menu dropdown-menu-end">
          @if (canInviteMembers) {
            <a
              href="javascript:void(0);"
              class="dropdown-item"
              (click)="inviteMembers()"
            >
              <i class="ri-user-add-line me-1"></i
              >{{ "chat.channel.invite" | translate }}
            </a>
          }
          @if (canArchiveConversation) {
            <a
              href="javascript:void(0);"
              class="dropdown-item"
              (click)="archiveConversation()"
            >
              <i class="ri-archive-line me-1"></i
              >{{ "chat.channel.archive" | translate }}
            </a>
          }
          @if (canLeaveConversation) {
            <a
              href="javascript:void(0);"
              class="dropdown-item text-danger"
              (click)="leaveConversation()"
            >
              <i class="ri-logout-box-r-line me-1"></i
              >{{ "chat.channel.leave" | translate }}
            </a>
          }
          @if (canDeleteConversation) {
            <div class="dropdown-divider"></div>
            <a
              href="javascript:void(0);"
              class="dropdown-item text-danger"
              (click)="deleteConversation()"
            >
              <i class="ri-delete-bin-line me-1"></i
              >{{ "chat.channel.delete" | translate }}
            </a>
          }
        </div>
      </div>
    }
  </div>

  <!-- Pinned messages bar (T-20 · RF-31/AC-27) -->
  @if (pinnedRows.length > 0) {
    <div
      class="chat-pinned-bar border-bottom px-3 py-1 d-flex align-items-center gap-2 flex-shrink-0"
    >
      <i class="ri-pushpin-2-line text-warning flex-shrink-0"></i>
      <button
        type="button"
        class="btn btn-link btn-sm p-0 text-body chat-name flex-grow-1 text-start"
        (click)="jumpToMessage(pinnedRows[0].messageId)"
      >
        @if (pinnedRows[0].authorName) {
          <span class="fw-semibold me-1">{{ pinnedRows[0].authorName }}:</span>
        }
        <span class="text-muted">{{ pinnedRows[0].excerpt }}</span>
      </button>
      <div class="dropdown flex-shrink-0">
        <button
          type="button"
          class="btn btn-sm btn-light chat-conversation__header-btn dropdown-toggle arrow-none"
          data-bs-toggle="dropdown"
          aria-expanded="false"
          [attr.aria-label]="'chat.conversation.pinnedList' | translate"
          [matTooltip]="'chat.conversation.pinnedList' | translate"
        >
          <span class="badge bg-soft-warning text-warning">{{
            pinnedRows.length
          }}</span>
        </button>
        <div class="dropdown-menu dropdown-menu-end chat-pinned-menu p-0">
          <h6 class="dropdown-header">
            {{ "chat.conversation.pinnedTitle" | translate }}
          </h6>
          @for (pinned of pinnedRows; track trackPinned($index, pinned)) {
            <div class="d-flex align-items-center gap-1 px-2 py-1">
              <button
                type="button"
                class="btn btn-link btn-sm p-0 text-body chat-name flex-grow-1 text-start"
                (click)="jumpToMessage(pinned.messageId)"
              >
                @if (pinned.authorName) {
                  <span class="fw-semibold me-1">{{ pinned.authorName }}:</span>
                }
                <span class="text-muted">{{ pinned.excerpt }}</span>
              </button>
              <button
                type="button"
                class="btn btn-sm btn-light flex-shrink-0"
                (click)="unpin(pinned)"
                [matTooltip]="'chat.conversation.unpin' | translate"
                [attr.aria-label]="'chat.conversation.unpin' | translate"
              >
                <i class="ri-close-line"></i>
              </button>
            </div>
          }
        </div>
      </div>
    </div>
  }

  <!-- Timeline + thread panel -->
  <div
    class="chat-conversation__main d-flex flex-grow-1 overflow-hidden position-relative"
  >
    <div
      class="chat-conversation__flow d-flex flex-column flex-grow-1 overflow-hidden"
    >
      <div
        #scrollContainer
        class="chat-conversation__timeline flex-grow-1 overflow-auto px-3 py-2"
        [class.chat-conversation__timeline--direct]="isDirect"
        (scroll)="onScroll()"
      >
        @if (loadingHistory && rows.length === 0) {
          <div
            class="d-flex flex-column align-items-center justify-content-center gap-2 py-5"
          >
            <div class="spinner-border text-primary" role="status"></div>
            <span class="text-muted">{{
              "chat.conversation.loadingHistory" | translate
            }}</span>
          </div>
        } @else if (loadError) {
          <div
            class="d-flex flex-column align-items-center justify-content-center gap-2 py-5 px-3 text-center"
          >
            <i class="ri-error-warning-line font-24 text-danger"></i>
            <span class="text-muted">{{
              "chat.conversation.loadError" | translate
            }}</span>
            <button
              type="button"
              class="btn btn-sm btn-soft-primary"
              (click)="retryLoad()"
            >
              {{ "chat.bubble.retry" | translate }}
            </button>
          </div>
        } @else {
          @if (loadingOlder) {
            <div class="text-center py-1">
              <div
                class="spinner-border spinner-border-sm text-primary"
                role="status"
              ></div>
            </div>
          }

          @if (isDraft) {
            <div
              class="d-flex flex-column align-items-center justify-content-center gap-2 py-5 px-3 text-center"
            >
              <div class="avatar-sm rounded-circle bg-soft-primary">
                <i
                  class="ri-chat-new-line avatar-title text-primary font-18"
                ></i>
              </div>
              <span class="text-muted">{{
                "chat.conversation.draftHint" | translate
              }}</span>
            </div>
          } @else if (rows.length === 0 && !loadingHistory) {
            <div
              class="d-flex flex-column align-items-center justify-content-center gap-2 py-5 px-3 text-center"
            >
              <div class="avatar-sm rounded-circle bg-soft-primary">
                <i
                  class="ri-chat-smile-2-line avatar-title text-primary font-18"
                ></i>
              </div>
              <span class="text-muted">{{
                "chat.conversation.empty" | translate
              }}</span>
            </div>
          }

          @for (row of rows; track row.key) {
            @if (row.kind === "divider") {
              <div class="chat-day-divider d-flex align-items-center my-2">
                <span class="flex-grow-1 border-top"></span>
                <span class="badge bg-soft-secondary text-secondary mx-2">{{
                  row.label
                }}</span>
                <span class="flex-grow-1 border-top"></span>
              </div>
            } @else if (row.kind === "unread") {
              <div
                class="chat-unread-divider d-flex align-items-center my-2"
                data-unread-line
                role="separator"
                [attr.aria-label]="'chat.conversation.newMessages' | translate"
              >
                <span class="chat-unread-divider__line flex-grow-1"></span>
                <span class="chat-unread-divider__label mx-2">{{
                  "chat.conversation.newMessages" | translate
                }}</span>
                <span class="chat-unread-divider__line flex-grow-1"></span>
              </div>
            } @else {
              <div
                class="chat-message d-flex gap-2 position-relative"
                [attr.data-message-id]="row.message._id"
                [class.chat-message--pending]="
                  row.message.sendState === 'sending'
                "
                [class.chat-message--highlight]="
                  highlightedMessageId === row.message._id
                "
                [class.chat-message--mentions-me]="row.mentionsMe"
                [class.chat-message--lead]="row.showAuthor"
                [class.chat-message--own]="row.own"
              >
                <!-- Avatar column — same tile as the conversation list, presence
                     dot included -->
                @if (row.showAuthor) {
                  <span
                    class="chat-avatar-tile flex-shrink-0"
                    [matTooltip]="authorTooltip(row)"
                  >
                    @if (row.authorImage) {
                      <img [src]="row.authorImage" [alt]="row.authorName" />
                    } @else {
                      {{ row.authorInitial }}
                    }
                    <span
                      class="chat-avatar-tile__presence rounded-circle"
                      [ngClass]="
                        authorPresence(row)?.dotClass ?? 'bg-secondary'
                      "
                      aria-hidden="true"
                    ></span>
                  </span>
                } @else {
                  <span
                    class="chat-message__avatar-spacer flex-shrink-0"
                    aria-hidden="true"
                  ></span>
                }

                <div class="chat-message__body overflow-hidden">
                  @if (row.showAuthor && !isDirect) {
                    <div class="d-flex align-items-baseline gap-2">
                      <span class="fw-semibold chat-name">{{
                        row.authorName
                      }}</span>
                      <!-- Names repeat in a busy channel; this is the one that is
                           worth spotting without reading it. -->
                      @if (row.own) {
                        <small class="text-muted flex-shrink-0">{{
                          "chat.dialog.yourself" | translate
                        }}</small>
                      }
                      <small class="text-muted flex-shrink-0">{{
                        row.timeLabel
                      }}</small>
                    </div>
                  }

                  <!-- Forwarded banner (RF-13) — identifies the copy and its
                       original author ("Encaminhada de X"). Suppressed on the
                       tombstone: "mensagem apagada" already says everything. -->
                  @if (row.forwardedLabel && !row.message.deleted) {
                    <small
                      class="chat-message__forwarded d-flex align-items-center gap-1 text-muted fst-italic"
                    >
                      <i class="ri-share-forward-line" aria-hidden="true"></i>
                      <span class="text-truncate">{{
                        row.forwardedLabel
                      }}</span>
                    </small>
                  }

                  <!-- Quote block (resolved locally; neutral placeholder otherwise) -->
                  @if (row.quoteAuthor || row.quoteText) {
                    <button
                      type="button"
                      class="chat-message__quote btn btn-link border-start ps-2 my-1 d-block text-start text-body w-100"
                      (click)="jumpToQuote(row)"
                      [attr.aria-label]="
                        'chat.conversation.jumpToQuoted' | translate
                      "
                    >
                      <small class="fw-semibold d-block text-truncate">{{
                        row.quoteAuthor
                      }}</small>
                      <small class="text-muted d-block text-truncate">{{
                        row.quoteText
                      }}</small>
                    </button>
                  } @else if (row.quoteMissing) {
                    <div class="chat-message__quote border-start ps-2 my-1">
                      <small class="text-muted fst-italic">{{
                        "chat.conversation.quoteUnavailable" | translate
                      }}</small>
                    </div>
                  }

                  @if (row.message.deleted) {
                    <p class="m-0 fst-italic text-muted">
                      {{ "chat.conversation.deletedMessage" | translate }}
                    </p>
                  } @else if (editingMessageId === row.message._id) {
                    <!-- Inline edit (RF-09) — Enter saves, Shift+Enter breaks, Esc cancels -->
                    <div class="chat-message__edit mt-1">
                      <textarea
                        class="form-control"
                        rows="2"
                        name="chatEditDraft"
                        [(ngModel)]="editDraft"
                        (keydown)="onEditKeydown($event)"
                        [attr.aria-label]="
                          'chat.conversation.editMessage' | translate
                        "
                      ></textarea>
                      <div class="d-flex justify-content-end gap-1 mt-1">
                        <button
                          type="button"
                          class="btn btn-sm btn-light"
                          (click)="cancelEdit()"
                        >
                          {{ "chat.dialog.cancel" | translate }}
                        </button>
                        <button
                          type="button"
                          class="btn btn-sm btn-primary"
                          (click)="saveEdit()"
                        >
                          {{ "chat.conversation.saveEdit" | translate }}
                        </button>
                      </div>
                    </div>
                  } @else {
                    <!-- Media cards (T-33) — type switch mirrors ChatMessageTypeEnum -->
                    @if (row.message.type === messageType.IMAGE) {
                      <!-- Responsive thumbnail — opens the viewer over the timeline's images -->
                      <button
                        type="button"
                        class="btn p-0 border-0 d-inline-block mt-1 chat-message__image-button"
                        (click)="openImage(row)"
                        [attr.aria-label]="row.message.fileName"
                      >
                        <img
                          class="chat-message__image rounded border"
                          [src]="row.message.fileUrl"
                          [alt]="row.message.fileName"
                        />
                      </button>
                    } @else if (row.message.type === messageType.VIDEO) {
                      <video
                        class="chat-message__video rounded border mt-1"
                        controls
                        preload="metadata"
                        [src]="row.message.fileUrl"
                      ></video>
                    } @else if (row.message.type === messageType.AUDIO) {
                      <!-- Audio player (T-34): waveform scrub, speed toggle, download.
                           Seeded by clientMessageId — stable across the optimistic →
                           persisted _id swap, so the waveform never redraws. -->
                      <chat-audio-player
                        [src]="row.message.fileUrl"
                        [messageId]="
                          row.message.clientMessageId || row.message._id
                        "
                        [fileName]="row.message.fileName"
                      ></chat-audio-player>
                    } @else if (row.message.type === messageType.DOCUMENT) {
                      <div
                        class="chat-message__file border rounded d-flex align-items-center gap-2 p-2 mt-1"
                      >
                        <span
                          class="avatar-xs rounded bg-soft-primary flex-shrink-0"
                        >
                          <i
                            class="avatar-title text-primary font-16"
                            [ngClass]="row.fileIcon"
                          ></i>
                        </span>
                        <div class="flex-grow-1 overflow-hidden">
                          <small class="d-block text-truncate fw-semibold">{{
                            row.message.fileName
                          }}</small>
                          <small class="text-muted d-block">{{
                            row.fileSizeLabel
                          }}</small>
                        </div>
                        <a
                          class="btn btn-sm btn-light flex-shrink-0"
                          [href]="row.message.fileUrl"
                          target="_blank"
                          rel="noopener noreferrer"
                          download
                          [matTooltip]="
                            'chat.conversation.download' | translate
                          "
                          [attr.aria-label]="
                            'chat.conversation.download' | translate
                          "
                        >
                          <i class="ri-download-2-line"></i>
                        </a>
                      </div>
                    } @else {
                      <p
                        class="m-0 chat-message__content"
                        [innerHTML]="
                          row.message.content | chatMarkdown: row.mentionRender
                        "
                      ></p>
                    }

                    <!-- Work reference (T-22): snapshot card deep-linking to the record -->
                    @if (row.message.contextRef) {
                      <chat-context-ref-card
                        [reference]="row.message.contextRef"
                      ></chat-context-ref-card>
                    }

                    <span
                      class="chat-message__meta d-flex align-items-center flex-wrap gap-2"
                    >
                      @if (isDirect) {
                        <small class="text-muted">{{ row.timeLabel }}</small>
                      }
                      @if (row.message.editedAt) {
                        <small class="text-muted">{{
                          "chat.conversation.edited" | translate
                        }}</small>
                      }
                      @if (row.pinned) {
                        <small
                          class="text-warning d-inline-flex align-items-center gap-1"
                        >
                          <i class="ri-pushpin-2-line"></i
                          >{{ "chat.conversation.pinnedFlag" | translate }}
                        </small>
                      }
                      @if (row.saved) {
                        <small
                          class="text-muted d-inline-flex align-items-center gap-1"
                        >
                          <i class="ri-bookmark-fill"></i
                          >{{ "chat.conversation.savedFlag" | translate }}
                        </small>
                      }
                      @if (row.message.sendState === "sending") {
                        <small
                          class="text-muted d-inline-flex align-items-center gap-1"
                        >
                          <i class="ri-time-line"></i
                          >{{ "chat.conversation.sending" | translate }}
                        </small>
                      } @else if (row.message.sendState === "sent") {
                        <!-- Direct conversations only: the counterpart's read watermark (RF-33/AC-29) -->
                        @if (isReadByCounterpart(row)) {
                          <small
                            class="text-primary d-inline-flex align-items-center gap-1"
                          >
                            <i class="ri-check-double-line"></i
                            >{{ "chat.conversation.read" | translate }}
                          </small>
                        } @else {
                          <small
                            class="text-muted d-inline-flex align-items-center gap-1"
                          >
                            <i class="ri-check-line"></i
                            >{{ "chat.conversation.sent" | translate }}
                          </small>
                        }
                      } @else if (row.message.sendState === "failed") {
                        <small
                          class="text-danger d-inline-flex align-items-center gap-1"
                        >
                          <i class="ri-error-warning-line"></i
                          >{{ "chat.conversation.sendFailed" | translate }}
                        </small>
                        <!-- Retry re-sends the SAME clientMessageId — idempotent, no duplication -->
                        <button
                          type="button"
                          class="btn btn-sm btn-soft-primary chat-message__retry"
                          (click)="retrySend(row)"
                        >
                          <i class="ri-restart-line me-1"></i
                          >{{ "chat.bubble.retry" | translate }}
                        </button>
                        <button
                          type="button"
                          class="btn btn-sm btn-light chat-message__retry"
                          (click)="discardSend(row)"
                        >
                          <i class="ri-delete-bin-line me-1"></i
                          >{{ "chat.conversation.discard" | translate }}
                        </button>
                      }
                    </span>
                  }

                  <!-- Reactions + thread chip -->
                  @if (
                    !row.message.deleted &&
                    (row.reactions.length > 0 || row.message.threadCount > 0)
                  ) {
                    <div class="d-flex align-items-center flex-wrap gap-1 mt-1">
                      @for (reaction of row.reactions; track reaction.emoji) {
                        <button
                          type="button"
                          class="btn btn-sm chat-reaction-chip"
                          [class.btn-soft-primary]="reaction.mine"
                          [class.btn-light]="!reaction.mine"
                          (click)="toggleReaction(row, reaction.emoji)"
                        >
                          <span>{{ reaction.emoji }}</span>
                          <small class="ms-1">{{ reaction.count }}</small>
                        </button>
                      }
                      @if (row.message.threadCount > 0) {
                        <!-- Opens the thread panel (RF-12/AC-10) -->
                        <button
                          type="button"
                          class="btn btn-sm chat-thread-chip d-inline-flex align-items-center gap-1"
                          [class.btn-soft-info]="row.threadUnseen"
                          [class.btn-light]="!row.threadUnseen"
                          (click)="openThread(row)"
                        >
                          <i class="ri-discuss-line"></i>
                          <span>{{
                            "chat.conversation.threadCount"
                              | translate: { count: row.message.threadCount }
                          }}</span>
                          @if (row.threadTimeLabel) {
                            <small class="text-muted">{{
                              row.threadTimeLabel
                            }}</small>
                          }
                        </button>
                      }
                    </div>
                  }
                </div>

                <!-- Hover/focus action toolbar (T-20). Always in the DOM so it is
                     keyboard reachable; CSS reveals it on hover/focus-within.
                     Hidden in the public-channel preview — every action needs
                     membership (react/reply/pin), so join comes first. -->
                @if (
                  !isChannelPreview &&
                  !row.pending &&
                  !row.message.deleted &&
                  editingMessageId !== row.message._id
                ) {
                  <div
                    class="chat-message__toolbar btn-group btn-group-sm"
                    role="group"
                    [attr.aria-label]="'chat.conversation.actions' | translate"
                  >
                    @for (emoji of topReactions; track emoji) {
                      <button
                        type="button"
                        class="btn chat-message__toolbar-btn"
                        (click)="toggleReaction(row, emoji)"
                        [attr.aria-label]="
                          'chat.conversation.reactWith'
                            | translate: { emoji: emoji }
                        "
                        [matTooltip]="
                          'chat.conversation.reactWith'
                            | translate: { emoji: emoji }
                        "
                      >
                        {{ emoji }}
                      </button>
                    }

                    <!-- Full emoji picker -->
                    <div class="btn-group btn-group-sm" role="group">
                      <button
                        type="button"
                        class="btn chat-message__toolbar-btn dropdown-toggle arrow-none"
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                        [attr.aria-label]="
                          'chat.conversation.addReaction' | translate
                        "
                        [matTooltip]="
                          'chat.conversation.addReaction' | translate
                        "
                      >
                        <i class="ri-emotion-line"></i>
                      </button>
                      <div
                        class="dropdown-menu dropdown-menu-end chat-reaction-menu p-1"
                      >
                        @for (emoji of quickReactions; track emoji) {
                          <button
                            type="button"
                            class="btn btn-sm btn-light chat-reaction-chip m-1"
                            (click)="toggleReaction(row, emoji)"
                          >
                            {{ emoji }}
                          </button>
                        }
                      </div>
                    </div>

                    <!--
                      Reply / forward / save / pin used to sit here as five more
                      icon buttons, which made the bar ten buttons wide — wide
                      enough to cover the author name and time it floats over. They
                      are one click deeper now, in the overflow menu below, where
                      they also get readable labels instead of icon-only guesses.
                    -->

                    <!-- Overflow menu -->
                    <div class="btn-group btn-group-sm" role="group">
                      <button
                        type="button"
                        class="btn chat-message__toolbar-btn dropdown-toggle arrow-none"
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                        [attr.aria-label]="
                          'chat.conversation.moreActions' | translate
                        "
                        [matTooltip]="
                          'chat.conversation.moreActions' | translate
                        "
                      >
                        <i class="ri-more-2-fill"></i>
                      </button>
                      <div class="dropdown-menu dropdown-menu-end">
                        <!-- Moved out of the floating bar: same handlers, now labelled. -->
                        <button
                          type="button"
                          class="dropdown-item"
                          (click)="openThread(row)"
                        >
                          <i class="ri-discuss-line me-2"></i
                          >{{ "chat.conversation.replyInThread" | translate }}
                        </button>
                        <button
                          type="button"
                          class="dropdown-item"
                          (click)="startQuoteReply(row)"
                        >
                          <i class="ri-corner-up-left-line me-2"></i
                          >{{ "chat.conversation.quoteReply" | translate }}
                        </button>
                        <button
                          type="button"
                          class="dropdown-item"
                          (click)="openForward(row)"
                        >
                          <i class="ri-share-forward-line me-2"></i
                          >{{ "chat.conversation.forward" | translate }}
                        </button>
                        <button
                          type="button"
                          class="dropdown-item"
                          (click)="toggleSaved(row)"
                        >
                          <i
                            class="me-2"
                            [ngClass]="
                              row.saved
                                ? 'ri-bookmark-fill'
                                : 'ri-bookmark-line'
                            "
                          ></i>
                          {{
                            (row.saved
                              ? "chat.conversation.unsave"
                              : "chat.conversation.save"
                            ) | translate
                          }}
                        </button>
                        @if (channelId) {
                          <button
                            type="button"
                            class="dropdown-item"
                            (click)="togglePin(row)"
                          >
                            <i
                              class="me-2"
                              [ngClass]="
                                row.pinned
                                  ? 'ri-pushpin-2-fill'
                                  : 'ri-pushpin-2-line'
                              "
                            ></i>
                            {{
                              (row.pinned
                                ? "chat.conversation.unpin"
                                : "chat.conversation.pin"
                              ) | translate
                            }}
                          </button>
                        }

                        <div class="dropdown-divider"></div>

                        @if (row.own && row.message.type === messageType.TEXT) {
                          <button
                            type="button"
                            class="dropdown-item"
                            (click)="startEdit(row)"
                          >
                            <i class="ri-pencil-line me-2"></i
                            >{{ "chat.conversation.editMessage" | translate }}
                          </button>
                        }
                        @if (row.own) {
                          <button
                            type="button"
                            class="dropdown-item text-danger"
                            (click)="confirmDelete(row)"
                          >
                            <i class="ri-delete-bin-line me-2"></i
                            >{{ "chat.conversation.deleteMessage" | translate }}
                          </button>
                        }
                        @if (!row.own && !isDirect) {
                          <button
                            type="button"
                            class="dropdown-item"
                            (click)="replyInPrivate(row)"
                          >
                            <i class="ri-user-received-line me-2"></i
                            >{{
                              "chat.conversation.replyInPrivate" | translate
                            }}
                          </button>
                        }
                        @if (row.copyText) {
                          <button
                            type="button"
                            class="dropdown-item"
                            (click)="copyText(row)"
                          >
                            <i class="ri-file-copy-line me-2"></i
                            >{{ "chat.conversation.copyText" | translate }}
                          </button>
                        }
                      </div>
                    </div>
                  </div>
                }
              </div>
            }
          }

          @if (typingLabel) {
            <div class="text-muted fst-italic py-1">
              <small>{{ typingLabel }}</small>
            </div>
          }

          <!-- Jump-to-latest pill. Sticky INSIDE the scroll container: its slot
               is the end of the content, so pinning to the bottom edge keeps it
               floating over the last visible rows while scrolled up — no
               absolute-positioning fight with the composer below. -->
          @if (showJumpPill) {
            <div class="chat-jump-anchor d-flex justify-content-center">
              <button
                type="button"
                class="chat-jump-pill btn btn-sm rounded-pill d-inline-flex align-items-center"
                (click)="jumpToLatest()"
                [attr.aria-label]="'chat.conversation.jumpToLatest' | translate"
              >
                <i class="ri-arrow-down-line" aria-hidden="true"></i>
                @if (newSinceScroll > 0) {
                  <span class="ms-1 fw-semibold">{{ newSinceScroll }}</span>
                }
              </button>
            </div>
          }
        }
      </div>

      <!-- Quote-reply strip (RF-30) — cleared on send or cancel -->
      @if (quoteTarget) {
        <div
          class="chat-quote-strip border-top px-3 py-1 d-flex align-items-center gap-2 flex-shrink-0"
        >
          <i class="ri-corner-up-left-line text-primary flex-shrink-0"></i>
          <div class="flex-grow-1 overflow-hidden">
            <small class="fw-semibold d-block text-truncate">{{
              quoteTarget.authorName
            }}</small>
            <small class="text-muted d-block text-truncate">{{
              quoteTarget.excerpt
            }}</small>
          </div>
          <button
            type="button"
            class="btn btn-sm btn-light flex-shrink-0"
            (click)="cancelQuoteReply()"
            [matTooltip]="'chat.conversation.cancelQuote' | translate"
            [attr.aria-label]="'chat.conversation.cancelQuote' | translate"
          >
            <i class="ri-close-line"></i>
          </button>
        </div>
      }

      <!-- Public-channel preview (DEV-1984): the composer gives way to the
           join call-to-action until a membership row exists. -->
      @if (isChannelPreview) {
        <div class="border-top p-3 text-center flex-shrink-0">
          <p class="text-muted small mb-2">
            {{ "chat.channel.previewHint" | translate }}
          </p>
          <button
            type="button"
            class="btn btn-primary"
            [disabled]="joiningChannel"
            (click)="joinChannel()"
          >
            @if (joiningChannel) {
              <span
                class="spinner-border spinner-border-sm me-1"
                role="status"
                aria-hidden="true"
              ></span>
            } @else {
              <i class="ri-user-add-line me-1" aria-hidden="true"></i>
            }
            {{ "chat.channel.joinCta" | translate }}
          </button>
        </div>
      } @else {
        <!-- Rich composer (T-19/T-33): toolbar + mentions + emoji + drafts + attachments -->
        <chat-composer
          class="flex-shrink-0"
          [disabled]="composerDisabled"
          [members]="mentionMembers"
          [channelId]="channelId"
          (send)="onComposerSend($event)"
          (typingChange)="onTypingChange($event)"
          (draftChange)="onDraftChange()"
          (editLast)="editLastOwn()"
        ></chat-composer>
      }
    </div>

    <!-- Thread panel (T-20): right drawer in the full view, full cover in the popup -->
    @if (openThreadRootId) {
      <div
        class="chat-conversation__thread border-start flex-shrink-0"
        [class.chat-conversation__thread--overlay]="compact"
      >
        <chat-thread
          [rootMessageId]="openThreadRootId"
          [channelId]="channelId"
          [compact]="compact"
          [members]="mentionMembers"
          [disabled]="composerDisabled"
          (closed)="closeThread()"
        ></chat-thread>
      </div>
    }
  </div>
</div>
